/**
 * **通用 BGM 指挥器。**
 *
 * ## 这个文件里不出现任何具体的游戏参数名
 *
 * 引擎只知道「有一组参数」和「一张声明式的乐谱」。`isFinal`、`isDying`、`energy`
 * 这些名字全部活在乐谱数据里（`score.*.ts`），换游戏只需要换乐谱。
 *
 * ## 三层结构
 *
 * ```
 *   cue（音乐状态）  ← 由规则按参数选出来，切换代价大
 *     └─ 主轨 + 叠加层 + 变体 + 随机层  ← 全部由参数驱动，切换只改增益
 * ```
 *
 * **关键设计：变体是「层」，不是「cue」。** 逆位版和正位版是同一首曲子的两个文件，
 * 两个播放器**同时启动、同步跑**，切换只是把增益从 0 拉到 1。
 * 好处是切变体**不丢播放位置**，也不会重新播引入段 —— 如果把它建模成换 cue，
 * 每次切换都会从头开始，这正是之前「isDying 没有首次」现象的根因。
 *
 * ## 过渡
 *
 * cue 之间的切换由目标 cue 的 `transitionIn` 描述（时机 / 退场方式 / 进场方式），
 * 支持「立即切」「对齐小节」「先播收尾再交接」等组合。见 `TransitionSpec`。
 */

import { Draw, Gain, Player, getContext, start as toneStart } from 'tone';
import { createBgmFx, type BgmFx } from './fx';
import { t } from '../i18n';

// ---------------------------------------------------------------- 类型 ----

/** 参数值。建议只用 number / boolean，字符串仅作兼容。 */
export type ParamValue = number | string | boolean;

/** 一组参数。键名由乐谱决定，引擎不关心。 */
export type ParamBag = Readonly<Record<string, ParamValue>>;

/** 音频文件上的一段区间（秒）。 */
export interface LoopRegion {
  /** 循环起点（秒） */
  start: number;
  /** 循环终点（秒） */
  end: number;
}

/** 一个候选音频源。 */
export interface AudioSource {
  /** 音频 URL */
  url: string;
  /**
   * MIME（含 codecs），用来问浏览器「你能不能解」。
   * 例：`audio/mp4; codecs="mp4a.40.2"` / `audio/ogg; codecs="opus"`
   */
  mime: string;
}

/** 一个音轨。 */
export interface TrackSpec {
  /**
   * 候选源，**按顺序**取第一个浏览器能解的。
   *
   * 为什么要有多个：不同浏览器的编解码器支持不一样。典型坑是
   * **Linux 上的 Chromium / Brave / ungoogled-chromium 常常不带 AAC**，
   * 只给 m4a 的话 `decodeAudioData` 会抛 `EncodingError: Unable to decode audio data`。
   * 再挂一个 ogg/opus 就能兜住（Chrome 系和 Firefox 都自带 Opus）。
   */
  sources: readonly AudioSource[];
  /** 曲速 */
  bpm: number;
  /** 每小节几拍 */
  beatsPerBar: number;
}

/**
 * 问浏览器能不能解这种 MIME。
 *
 * `canPlayType` **不是权威答案**（可能回 "maybe" 然后解不开），所以它只用来
 * 给候选源排序；真正的兜底是 `load()` 失败后依次试下一个源。
 * 非浏览器环境（单测跑在 node 里）直接当支持处理。
 */
/**
 * **让出一次"空闲时间"**（后台预加载用）。
 *
 * 有 `requestIdleCallback` 就用它（`timeout` 兜底：再忙也得在 1.5 秒内轮到），
 * 没有就退回 `setTimeout(0)`。
 */
function idle(): Promise<void> {
  return new Promise((resolve) => {
    const ric = (
      globalThis as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(() => resolve(), { timeout: 1500 });
      return;
    }
    setTimeout(resolve, 0);
  });
}

/** 单调时钟（毫秒）。没有 `performance` 的环境退回 `Date.now()` */
function perfNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

export function canDecodeMime(mime: string): boolean {
  if (typeof document === 'undefined') return true;
  const probe = document.createElement('audio');
  return probe.canPlayType(mime) !== '';
}

// ------------------------------------------------------------ 层的种类 ----

/**
 * **参数驱动的叠加层**：条件成立时淡入，不成立时淡出。
 *
 * 多个条件层可以同时响。原版 RADIANCE 的「大厅逆位」就是这种 ——
 * floor1 继续播，floor1-reverse 叠上来。
 */
export interface ConditionalLayerSpec {
  /** 音轨 id */
  track: string;
  /** 条件满足时这层响 */
  when: ParamBag;
  /** 淡入 / 淡出秒数（留空则用 0.5） */
  fade?: number;
}

/**
 * **互斥变体**：按顺序取第一个条件成立的，同一时刻只有一个响。
 *
 * 原版 RADIANCE 的「第九层逆位」是这种 —— floor9 和 floor9-reverse 是
 * 同一首曲子的两个编曲，**整轨替换**。
 *
 * 两个播放器同时起播、同步跑，切换只改增益，所以**位置不丢**。
 */
export interface VariantSpec {
  /** 按顺序取第一个 `when` 匹配的 */
  options: readonly { track: string; when: ParamBag }[];
  /** 切换淡化秒数。0 = 硬切 */
  fade: number;
}

/**
 * 随机叠加层。
 *
 * 从 `pool` 里随机挑若干层同时播放，隔一段时间重掷。原版 RADIANCE 用它给
 * 大厅加「每次听都不一样」的织体（6 个 spec 层里同时开 2–3 个，20–60 秒换一次）。
 */
export interface RandomLayerSpec {
  /** 候选音轨 id */
  pool: readonly string[];
  /**
   * 由参数算出「同时开几层」的上下界（含）。
   * 引擎在 `[floor(min), floor(max)]` 里取随机整数，再夹到 `[0, pool.length]`。
   */
  count: (params: ParamBag) => { min: number; max: number };
  /** 重掷间隔（秒），在区间内随机 */
  reroll: readonly [number, number];
  /** 单层淡入 / 淡出秒数 */
  fade: number;
}

/** 切换时机。 */
export type TransitionAt = 'immediate' | 'bar';
/** 旧 cue 的退场方式。 */
export type TransitionOut = 'fade' | 'cut' | 'outro';
/** 新 cue 的进场方式。 */
export type TransitionIn = 'fade' | 'cut' | 'after';

/**
 * **怎么切进这个 cue。**
 *
 * 语义上写的是「新 cue 的进场方式」，但退场方式也跟着它走 —— 因为两者是一件事的
 * 两面：交叉淡化、硬切、还是「旧的先走完新的再上」。
 *
 * | 字段 | 取值 | 含义 |
 * |---|---|---|
 * | `at` | `immediate` / `bar` | 何时开始切。默认 `bar` |
 * | `out` | `fade` | 旧的淡出 `outFade` 秒后停 |
 * | | `cut` | 旧的立刻停 |
 * | | `outro` | 旧的先播一次自己的 `outro` 段（没配就退化成 `cut`） |
 * | `in` | `fade` | 新的从 0 淡入 `inFade` 秒 |
 * | | `cut` | 新的立刻满音量 |
 * | | `after` | 等旧的走完（淡出结束 / 收尾播完）再满音量起 |
 */
export interface TransitionSpec {
  at?: TransitionAt;
  out?: TransitionOut;
  outFade?: number;
  in?: TransitionIn;
  inFade?: number;
}

/** 一个「音乐状态」。 */
export interface CueSpec {
  /** 主轨 id */
  track: string;
  /** 循环区间。省略 = 整段文件循环 */
  loop?: LoopRegion;
  /**
   * 进入时**先从这里播一次**，播到 `loop.end` 之后才回 `loop.start` 循环。
   *
   * 用来实现「引入段」。**要求 `playFrom → loop.start` 这段在文件里是连续的**
   * —— Web Audio 的循环语义正好是「从 offset 播到 loopEnd 才跳回 loopStart」，
   * 所以只要把起点往前挪就行，不用两套播放器。
   */
  playFrom?: number;
  /** 收尾段。`transitionIn.out = 'outro'` 时播一次 */
  outro?: LoopRegion;
  /** 静态叠加层（无条件，进入就响） */
  overlays?: readonly string[];
  /** 参数驱动的叠加层 */
  layers?: readonly ConditionalLayerSpec[];
  /** 互斥变体 */
  variant?: VariantSpec;
  /** 随机叠加层 */
  randomLayers?: RandomLayerSpec;
  /** 从**别的** cue 切进来时的过渡方式 */
  transitionIn?: TransitionSpec;
  /** 淡入秒数，默认 0.4（`transitionIn.in='fade'` 且没写 `inFade` 时用） */
  fadeIn?: number;
  /** 淡出秒数，默认 0.4（`transitionIn.out='fade'` 且没写 `outFade` 时用） */
  fadeOut?: number;
}

/**
 * 一条选曲规则。
 *
 * **按数组顺序求值，先匹配者胜**，所以把更具体的规则写在前面。
 * `when: {}` 是合法的，会匹配一切 —— 拿来当兜底。
 */
export interface CueRule {
  when: ParamBag;
  cue: string;
}

/** 乐谱。换游戏只换这个对象。 */
export interface BgmScore {
  tracks: Readonly<Record<string, TrackSpec>>;
  cues: Readonly<Record<string, CueSpec>>;
  rules: readonly CueRule[];
  defaultCue: string;
  /**
   * 这些参数出现 **false → true 上升沿**时，登记「下一小节把当前 cue 拉回循环起点」。
   *
   * ⚠️ **每个阶段只生效一次**：消费过一次之后闩锁闭合并保持，
   * 直到 `clearRestartOnChange` 里的参数发生变化才重新武装。
   * 这正是「首次为真时重置」和「isFinal 变化时重置次数」的语义。
   */
  restartOnRisingEdge?: readonly string[];
  /**
   * 这些参数**值发生变化**时，把上面那个闩锁重新武装（并作废未执行的重启）。
   *
   * 例如 `['isFinal']` —— 进入终局后，濒死的重启重新计一次。
   */
  clearRestartOnChange?: readonly string[];
  /** `phrase` 事件的间隔，单位小节，默认 2 */
  phraseBars?: number;
}

// ---------------------------------------------------------------- 事件 ----

export interface BeatEvent {
  time: number;
  bar: number;
  beatInBar: number;
  beatsPerBar: number;
}

export interface BarEvent {
  time: number;
  bar: number;
  barInPhrase: number;
}

export interface PhraseEvent {
  time: number;
  bar: number;
  phrase: number;
  bars: number;
}

export interface CueEvent {
  time: number;
  cue: string;
  previous: string | null;
}

export interface RestartEvent {
  time: number;
}

export interface BgmEvents {
  beat: BeatEvent;
  bar: BarEvent;
  phrase: PhraseEvent;
  cue: CueEvent;
  restart: RestartEvent;
}

export type Unsubscribe = () => void;

/** 给 UI 用的即时读数，语义对齐 RADIANCE 的 `BgmDynGetTiming()`。 */
export interface BgmTiming {
  bar: number;
  beatInBar: number;
  beatsPerBar: number;
  progressToNextBeat: number;
  progressToNextBar: number;
}

// -------------------------------------------------------------- 小工具 ----

function isOn(v: ParamValue | undefined): boolean {
  return v === true || v === 1 || v === '1' || v === 'true';
}

function matches(params: ParamBag, when: ParamBag): boolean {
  for (const key of Object.keys(when)) {
    if (params[key] !== when[key]) return false;
  }
  return true;
}

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

function pickRandom<T>(items: readonly T[]): T | null {
  if (items.length === 0) return null;
  return items[Math.floor(Math.random() * items.length)] ?? null;
}

function randomInt(lo: number, hi: number): number {
  if (hi <= lo) return lo;
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

/**
 * 按乐谱规则算出该播哪个 cue。
 *
 * 纯函数 —— 游戏侧可以拿它做「当前参数会播哪首」的预查，单测也能直接覆盖。
 */
export function resolveCue(score: BgmScore, params: ParamBag): string {
  for (const rule of score.rules) {
    if (matches(params, rule.when)) return rule.cue;
  }
  return score.defaultCue;
}

/** 极简类型化事件总线。 */
class Emitter<E extends object> {
  private readonly listeners = new Map<keyof E, Set<(payload: never) => void>>();

  on<K extends keyof E>(event: K, fn: (payload: E[K]) => void): Unsubscribe {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    const raw = fn as (payload: never) => void;
    set.add(raw);
    return () => {
      set.delete(raw);
    };
  }

  protected emit<K extends keyof E>(event: K, payload: E[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of [...set]) (fn as (p: E[K]) => void)(payload);
  }

  protected dropAllListeners(): void {
    this.listeners.clear();
  }
}

// -------------------------------------------------------------- 运行时 ----

interface Layer {
  readonly player: Player;
  readonly gain: Gain;
  readonly region: LoopRegion;
}

interface CueRuntime {
  readonly spec: CueSpec;
  readonly track: TrackSpec;
  /** 主轨 + 静态叠加层 */
  readonly base: readonly Layer[];
  /**
   * 参数驱动的叠加层。
   *
   * ⚠️ **不是 `readonly` 数组**：首屏只建"马上可能响"的那几层，
   * 条件层与随机池由 `completeCue()` **原地往后追加**（顺序只增不改，
   * 于是 `randomOn` 里那些下标一直有效）。
   */
  readonly conditional: { layer: Layer; when: ParamBag; fade: number }[];
  /** 互斥变体 */
  readonly variants: readonly { layer: Layer; when: ParamBag }[];
  /** 随机层。同上：后台补齐时**往后追加**，不重排 */
  readonly random: Layer[];
  readonly randomOn: Set<number>;
  nextReroll: number;
}

const LOOKAHEAD = 0.15;
/**
 * 渐强那一档的斜坡时长（秒）。
 *
 * ⚠️ 比音量那条（0.08）**短**：渐强是每帧按包络推的，斜坡长了会跟不上
 *    （峰值到不了顶点）；短一点它才真的"顶到最大"。
 */
const BOOST_RAMP_SECONDS = 0.02;
const TICK_MS = 25;
const STALE = 0.25;
const START_OFFSET = 0.05;
/** 收尾段播完后留一点余量，避免切在采样点上产生爆音。 */
const TAIL = 0.05;
/** 条件层默认淡化时长。 */
const CONDITIONAL_FADE = 0.5;
/** 默认过渡：对齐小节、交叉淡化。 */
const DEFAULT_TRANSITION = { at: 'bar', out: 'fade', in: 'fade' } as const;

export class BgmDirector extends Emitter<BgmEvents> {
  private readonly score: BgmScore;
  private readonly master: Gain;
  /**
   * **BGM 的"播放故障"滤镜 + 混响**（见 `audio/fx.ts`）。
   *
   * ⚠️ **懒建**：没人抽到异常牌的话，这一串节点一个都不存在（也就顺带避开了
   *    单测那种没有音频环境的场合 —— 建不出来就是 `null`，一切照旧）。
   */
  private fx: BgmFx | null = null;
  /** 渐强倍数（1 = 不动）。见 `setBoost` */
  private boostValue = 1;

  // ---- 加载读数（见 `loadStats` 的说明）----
  /** 起播那个 cue 解完用了多久（毫秒） */
  private initialMs: number | null = null;
  /** 全部 cue 解完用了多久（毫秒）；后台还在补时是 `null` */
  private totalMs: number | null = null;
  /** 已经建好的 cue（含后台补的） */
  private readonly built = new Set<string>();
  /** 连条件层与随机池都建好了的 cue */
  private readonly complete = new Set<string>();
  /** 解码出来的 PCM 总量（字节）——"空间换时间"里那个"空间" */
  private pcmBytes = 0;
  /** 解了几条轨（同一条轨在不同 cue 里会各解一次） */
  private tracksDecoded = 0;
  /** 后台补那一条链。`dispose()` 之后不再继续 */
  private warm: Promise<void> | null = null;

  /** 后台那一条链（测试/调试可以 await 它，看"全部解完"要多久） */
  get warmPromise(): Promise<void> | null {
    return this.warm;
  }
  private readonly runtimes = new Map<string, CueRuntime>();
  private readonly chosenSources = new Map<string, AudioSource>();
  private readonly phraseBars: number;

  private params: Record<string, ParamValue>;
  private activeCue: string | null = null;
  private pendingCue: string | null = null;

  private pendingRestart = false;
  /** 「本阶段已经消费过重启」的闩锁。`clearRestartOnChange` 参数一变就重新武装。 */
  private restartLatched = false;
  /** 主音量。记着它是因为 `Gain` 上读回来的值在斜坡途中不是目标值 */
  private volumeValue = 1;

  private barEpoch = 0;
  /**
   * **这一轮 epoch 对应的文件偏移**（秒）。
   *
   * 起播时 = `playFrom`（引入段起点）；`performRestart()` 之后 = `loop.start`。
   * ⚠️ 晚到的层（`completeCue` 才建出来的条件层 / 随机池）要"接进来"就得知道
   *    主轨现在播到文件的哪儿了 —— 见 `lateJoinTime()`。
   */
  private posAtEpoch = 0;
  private barDur = 0;
  private beatsPerBar = 4;
  private nextBarIndex = 0;
  /** 延迟交接的小节时钟：等旧 cue 淡出 / 收尾播完才切过去 */
  private pendingClock: { at: number; runtime: CueRuntime } | null = null;
  private generation = 0;
  /**
   * 过渡序号，只被 `beginTransition` 递增。
   * cue 事件用它做守卫而不是 `generation` —— 因为延迟交接时 `setClock`
   * 会在交接点把 `generation` 再 ++ 一次，把刚排出去的 cue 事件误杀。
   */
  private transitionSeq = 0;

  private tickerId: ReturnType<typeof setInterval> | null = null;
  private loaded = false;
  private running = false;
  private disposed = false;

  constructor(score: BgmScore, initialParams: ParamBag = {}) {
    super();
    this.score = score;
    this.phraseBars = score.phraseBars ?? 2;
    this.params = { ...initialParams };
    this.master = new Gain(1).toDestination();
  }

  // ------------------------------------------------------------ 生命周期 ----

  /**
   * **预加载 + 解码。**
   *
   * 评审：
   *
   * > 每次刷新页面都要重新解码 bgm，我们是否能空间换时间，用 localStorage
   * > 等方式存储 bgm，然后闲时后台检查 bgm 文件是否更新？
   *
   * 先说结论（细节见 `docs/dynamic-music.md` §6）：**解码出来的 PCM 存不进
   * 任何浏览器存储**（`localStorage` 只存字符串、IndexedDB 只存字节，
   * 而 AudioBuffer 是内存里的东西），而编码字节那一半**浏览器 HTTP 缓存
   * 已经在做**（产物的文件名带内容 hash → 永远不会拿到旧文件，所以也不需要
   * "闲时检查更新"）。真正能省的是**别把全部音轨都堵在首屏**——
   * 所以这里改成：
   *
   * 1. **只等当前那一个 cue**（`resolveCue(params)` 选中的那个）—— 它才是
   *    马上要响的。这一步一完 `load()` 就 resolve，`start()` 可以立刻起播；
   * 2. 剩下的 cue 在**后台**按顺序补（一条轨一条轨地解，不和首屏抢带宽）。
   *
   * ⚠️ 后台补的那一段**不会**让"切曲"落空：`tick()` 里目标 cue 还没解好时
   * 会把 `pendingCue` **留在原地等**，而不是丢掉那一跳。
   */
  async load(): Promise<void> {
    if (this.disposed) throw new Error(t('audio.bgm.disposed'));
    if (this.loaded) return;

    // 引用检查只读乐谱数据，不用等解码 —— 放前面，坏乐谱立刻炸
    for (const rule of this.score.rules) {
      if (!(rule.cue in this.score.cues)) {
        throw new Error(t('audio.bgm.ruleMissingCue', { cue: rule.cue }));
      }
    }
    if (!(this.score.defaultCue in this.score.cues)) {
      throw new Error(t('audio.bgm.defaultCueMissing', { cue: this.score.defaultCue }));
    }

    const initial = resolveCue(this.score, this.params);
    const initialSpec = this.score.cues[initial];
    if (initialSpec === undefined) {
      throw new Error(t('audio.bgm.initialCueMissing', { cue: initial }));
    }
    const startedAt = perfNow();
    await this.buildRuntime(initial, initialSpec, true);
    this.initialMs = perfNow() - startedAt;
    this.loaded = true;

    // 其余在后台补（不 await）
    this.warm = this.warmRest(initial, startedAt);
  }

  /** 后台按顺序把剩下的 cue 解出来（见 `load()` 的说明） */
  private async warmRest(initial: string, startedAt: number): Promise<void> {
    // ① 先把初始 cue 省下的那几层补上（条件层 / 随机池）
    try {
      await this.completeCue(initial);
    } catch (error) {
      // 引擎层不引 Pixi 的 warn，也不该有 UI 依赖；控制台说一句就够
      console.warn(t('audio.bgm.warmCueFailed', { cue: initial, error: String(error) }));
    }
    /**
     * ★ **其余的 cue 等一个"闲时"再解。**
     *
     * 初始 cue 那几层是**马上可能响**的（逆位层随时会被 `isDying` 打开、随机池
     * 一进对局就要用），所以它们抢在前面解；而别的 cue（终局）通常要等玩家操作
     * 几分钟才轮到 —— 那就让浏览器**空闲时**再解，别和首屏抢 CPU。
     */
    await idle();
    // ② 再建其余的 cue
    for (const cueId of Object.keys(this.score.cues)) {
      if (cueId === initial || this.disposed) continue;
      const spec = this.score.cues[cueId];
      if (spec === undefined) continue;
      try {
        await this.buildRuntime(cueId, spec);
      } catch (error) {
        // ⚠️ 一个"还没轮到它响"的 cue 解不出来，不该把整个应用带下去：
        //    记一条，继续补下一个。真到要用它的时候，`tick()` 会一直等
        //    （不会切到半截），诊断里也看得见。
        // 引擎层不引 Pixi 的 warn，也不该有 UI 依赖；控制台说一句就够
        console.warn(t('audio.bgm.preloadCueFailed', { cue: cueId, error: String(error) }));
      }
    }
    if (!this.disposed) this.totalMs = perfNow() - startedAt;
  }

  /**
   * **把 `buildRuntime(..., essential)` 省下的那几层补上**（后台）。
   *
   * 补完之后如果这一 cue 正在响，会顺手让随机层**立刻重掷一次** ——
   * 否则"进对局（energy > 0）"那一跳可能刚好落在池子还没好的时候，
   * 而下一个重掷窗口是 20–60 秒之后：那段时间里随机层一直是空的。
   */
  /**
   * **把 `buildRuntime(..., essential)` 省下的那几层补上，并且让它们"接进"正在播的
   * 那一轮。**
   *
   * ⚠️ **这是懒加载最容易漏的一刀**：`startCueAt()` 只对"当时已经存在"的层调
   *    `player.start()`。晚建出来的条件层（逆位）和随机池如果只是 push 进数组、
   *    摆好增益，**播放器永远是 stopped** —— 增益亮着（诊断里 `audible: true`）、
   *    一点声音都没有。评审报的"切到 floor1-reverse / floor9 就不响了"就是它。
   *
   * 所以这里补一趟"半路加入"：算出主轨现在播到文件的哪儿（`lateJoinTime()`），
   * 从那个偏移起播，并把增益按当前参数摆好。
   *
   * ⚠️ `startCueAt()` 的 offset 与所有层共享同一条时间轴，所以"半路加入"必须
   *    真的按同一个偏移接上去 —— 不能从头播（那样会和主轨错开几十秒）。
   */
  private async completeCue(cueId: string): Promise<void> {
    const runtime = this.runtimes.get(cueId);
    if (runtime === undefined || this.disposed) return;
    const spec = runtime.spec;

    const added: Layer[] = [];
    for (const entry of spec.layers ?? []) {
      const layer = await this.makeLayer(entry.track, spec.loop, cueId);
      runtime.conditional.push({ layer, when: entry.when, fade: entry.fade ?? CONDITIONAL_FADE });
      added.push(layer);
    }
    for (const trackId of spec.randomLayers?.pool ?? []) {
      const layer = await this.makeLayer(trackId, spec.loop, cueId);
      runtime.random.push(layer);
      added.push(layer);
    }

    this.complete.add(cueId);
    const now = this.audioNow();
    // 正在播的那一轮：把新层接到**当前偏移**上（否则它们永远不会出声）
    if (this.running && this.activeCue === cueId) {
      const at = this.lateJoinTime(now);
      for (const layer of added) {
        layer.player.loop = true;
        layer.player.stop(at.time);
        layer.player.start(at.time + 0.001, at.offset);
        // 先静音，随后的 `refreshLayers`（条件层）/ 重掷（随机层）再把它抬起来
        layer.gain.gain.cancelScheduledValues(at.time);
        layer.gain.gain.setValueAtTime(0, at.time);
      }
      // 新来的条件层要按当前参数摆好增益；随机层下一个 tick 就能重掷
      this.refreshLayers(runtime, at.time);
    }
    runtime.nextReroll = Math.min(runtime.nextReroll, now);
  }

  /**
   * **"半路加入"的起播时刻与文件偏移。**
   *
   * 主轨从 `posAtEpoch` 起步，`barEpoch` 是那一刻的音频时钟 —— 所以现在播到
   * `posAtEpoch + (now - barEpoch)`。越过循环终点之后按循环长度折回。
   *
   * ⚠️ 还在**引入段**（`pos < loop.start`）时不能就地起播：Tone 的
   *    `Player._start` 在 `loop = true` 时会把 offset 夹回 `[loopStart, loopEnd]`，
   *    于是这一层会从循环开头开始、和主轨错开整段引入。这种情况下**等到主轨
   *    进循环那一刻**再起播（那时 offset 正好等于 `loop.start`，不需要夹）。
   */
  private lateJoinTime(now: number): { time: number; offset: number } {
    const runtime = this.activeCue === null ? undefined : this.runtimes.get(this.activeCue);
    const loop = runtime?.spec.loop;
    const pos = this.posAtEpoch + Math.max(0, now - this.barEpoch);
    if (loop === undefined) return { time: now, offset: pos };
    if (pos < loop.start) {
      // 还在引入段：等主轨进循环（`pos` 到 `loop.start` 的那一刻）
      return { time: now + (loop.start - pos), offset: loop.start };
    }
    if (pos <= loop.end) return { time: now, offset: pos };
    const len = Math.max(0.001, loop.end - loop.start);
    return { time: now, offset: loop.start + ((pos - loop.start) % len) };
  }

  /**
   * **加载读数**（冒烟与控制台用）。
   *
   * - `initialMs`：只等当前那一个 cue 花了多久 —— 它就是"刷新页面到能出声"的等待
   * - `totalMs`：全部 cue 解完花了多久（后台补齐；还没补完时是 `null`）
   * - `built` / `pending`：哪些 cue 已经就绪
   * - `pcmBytes`：解码出来的 PCM 总量。⚠️ 它是"空间换时间"里那个**空间** ——
   *   压缩音频解成 PCM 会涨大约一个数量级，而这个东西**存不进任何浏览器存储**
   */
  get loadStats(): {
    readonly initialMs: number | null;
    readonly totalMs: number | null;
    readonly built: readonly string[];
    readonly complete: readonly string[];
    readonly pending: readonly string[];
    readonly pcmBytes: number;
    readonly tracksDecoded: number;
    readonly initialCue: string | null;
  } {
    return {
      initialMs: this.initialMs === null ? null : Math.round(this.initialMs),
      totalMs: this.totalMs === null ? null : Math.round(this.totalMs),
      built: [...this.built],
      complete: [...this.complete],
      pending: Object.keys(this.score.cues).filter((id) => !this.built.has(id)),
      pcmBytes: this.pcmBytes,
      tracksDecoded: this.tracksDecoded,
      initialCue: this.initialCueBuilt,
    };
  }

  /** 起播时用的那个 cue（读数用） */
  private initialCueBuilt: string | null = null;

  /** 开始播放。**必须在用户手势里调**（浏览器自动播放策略）。重复调用安全。 */
  async start(): Promise<void> {
    if (this.disposed) throw new Error(t('audio.bgm.disposed'));
    if (this.running) return;
    if (!this.loaded) await this.load();

    await toneStart();
    this.running = true;

    // 首次进入没有旧 cue，不走 transitionIn，直接满音量起
    const cueId = resolveCue(this.score, this.params);
    const runtime = this.runtimes.get(cueId);
    if (runtime) {
      const at = this.audioNow() + START_OFFSET;
      this.startCueAt(runtime, at, 0);
      this.activeCue = cueId;
      this.setClock(runtime, at);
    }
    this.tickerId = setInterval(() => this.tick(), TICK_MS);
  }

  stop(): void {
    if (this.tickerId !== null) {
      clearInterval(this.tickerId);
      this.tickerId = null;
    }
    this.running = false;
    this.pendingCue = null;
    this.pendingRestart = false;
    this.pendingClock = null;
    const now = this.audioNow();
    for (const runtime of this.runtimes.values()) this.silence(runtime, now);
    this.activeCue = null;
    this.generation++;
  }

  dispose(): void {
    if (this.disposed) return;
    this.stop();
    this.dropAllListeners();
    for (const runtime of this.runtimes.values()) {
      for (const layer of layersOf(runtime)) {
        layer.player.dispose();
        layer.gain.dispose();
      }
    }
    this.runtimes.clear();
    this.warm = null;
    this.fx?.dispose();
    this.fx = null;
    this.master.dispose();
    this.disposed = true;
  }

  // ---------------------------------------------------------------- 参数 ----

  /**
   * 更新参数。引擎做四件事：
   *
   * 1. 重新算各「叠加层 / 变体」的增益（**只改增益，不重播**）
   * 2. `clearRestartOnChange` 里的参数变了 → 重新武装重启闩锁
   * 3. `restartOnRisingEdge` 的上升沿 + 闩锁未闭合 → 登记下一小节重启
   * 4. 该换 cue 了 → 按目标 cue 的 `transitionIn` 登记切换
   */
  setParams(patch: ParamBag): void {
    const previous = this.params;
    const next: Record<string, ParamValue> = { ...previous, ...patch };
    this.params = next;

    let changed = false;
    for (const key of Object.keys(patch)) {
      if (previous[key] !== next[key]) changed = true;
    }

    for (const name of this.score.clearRestartOnChange ?? []) {
      if (previous[name] !== next[name]) {
        this.restartLatched = false;
        this.pendingRestart = false;
        break;
      }
    }

    for (const name of this.score.restartOnRisingEdge ?? []) {
      if (!isOn(previous[name]) && isOn(next[name]) && !this.restartLatched) {
        this.pendingRestart = true;
        this.restartLatched = true;
      }
    }

    if (!this.running) return;

    if (changed) {
      const now = this.audioNow();
      for (const runtime of this.runtimes.values()) {
        this.refreshLayers(runtime, now);
        // 参数一变就让随机层在下一个小节重掷，否则改 energy 要等 20–60 秒才有反应
        runtime.nextReroll = Math.min(runtime.nextReroll, now);
      }
    }

    const target = resolveCue(this.score, this.params);
    this.pendingCue = target === this.activeCue ? null : target;
  }

  setParam(name: string, value: ParamValue): void {
    this.setParams({ [name]: value });
  }

  getParams(): ParamBag {
    return { ...this.params };
  }

  // ---------------------------------------------------------------- 音量 ----

  /**
   * **主音量**（0–1）。设置页的「BGM 音量」滑到这里。
   *
   * ⚠️ 音量 0 **不等于停播**：音乐照常走小节时钟，只是听不见。
   * 这与 `setParams` 里"只改增益、不重播"是同一条道理 ——
   * 若为了静音去 `stop()`，把音量拉回来时音乐就**从循环开头重来**了，
   * 而玩家只是把滑块拖到 0 再拖回来。
   */
  /**
   * **BGM 的"播放故障"滤镜 + 混响**（评审：自 IMPACT 时刻达到最大，随后衰减消失）。
   *
   * @param amount 0..1。0 = 完全透明（平时就是 0）
   *
   * ⚠️ 第一次 `amount > 0` 时才把那串节点接进来：`master` 从"直连输出"
   *    改成"先进这一串"。没人抽到异常牌的话，这一串节点一个都不存在。
   */
  setFx(amount: number): void {
    if (this.disposed) return;
    const wanted = Number.isFinite(amount) ? Math.min(Math.max(amount, 0), 1) : 0;
    if (this.fx === null) {
      if (wanted <= 0) return;
      const built = createBgmFx();
      // 没有可用的音频环境（单测、被策略拦）：当没这回事
      if (built === null) return;
      this.master.disconnect();
      this.master.connect(built.input);
      this.fx = built;
    }
    this.fx.setAmount(wanted);
  }

  /** 现在生效的故障滤镜剂量（读数 / 冒烟用） */
  get fxAmount(): number {
    return this.fx?.amount ?? 0;
  }

  /**
   * **音量增益倍数**（1 = 不动）。评审：「IMPACT 时要有逐渐上升的音量增益」。
   *
   * ⚠️ 它**乘**在 `volume` 上，而且**只加不减**（`Math.max(1, factor)`）——
   *    这条路上不许出现"把音乐掐掉"，第三轮删掉的留白就是那样把无缝循环切成
   *    硬切的。渐强只往上推。
   */
  setBoost(factor: number): void {
    if (this.disposed) return;
    this.boostValue = Number.isFinite(factor) ? Math.max(1, factor) : 1;
    this.master.gain.rampTo(this.volumeValue * this.boostValue, BOOST_RAMP_SECONDS);
  }

  /** 现在生效的增益倍数（读数 / 冒烟用） */
  get boost(): number {
    return this.boostValue;
  }

  setVolume(value: number): void {
    if (this.disposed) return;
    const clamped = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
    this.volumeValue = clamped;
    // 斜坡而不是直接赋值：直接跳变在音频里是一声"咔"
    this.master.gain.rampTo(clamped * this.boostValue, 0.08);
  }

  get volume(): number {
    return this.volumeValue;
  }

  // ---------------------------------------------------------------- 读数 ----

  get cue(): string | null {
    return this.activeCue;
  }

  get isRunning(): boolean {
    return this.running;
  }

  audioNow(): number {
    return getContext().currentTime;
  }

  getTiming(): BgmTiming | null {
    if (!this.running || this.barDur <= 0) return null;
    const elapsed = Math.max(0, this.audioNow() - this.barEpoch);
    const beats = elapsed / (this.barDur / this.beatsPerBar);
    return {
      bar: Math.floor(beats / this.beatsPerBar),
      beatInBar: Math.floor(beats) % this.beatsPerBar,
      beatsPerBar: this.beatsPerBar,
      progressToNextBeat: 1 - (beats % 1),
      progressToNextBar: 1 - ((beats / this.beatsPerBar) % 1),
    };
  }

  /** 每个音轨实际用的是哪个源（诊断用）。 */
  getSourceInfo(): ReadonlyArray<{ track: string; url: string; mime: string }> {
    return [...this.chosenSources.entries()].map(([track, source]) => ({
      track,
      url: source.url,
      mime: source.mime,
    }));
  }

  /** 诊断用：每个 cue 每一层现在的增益和播放器状态。 */
  getDiagnostics(): ReadonlyArray<{
    cue: string;
    track: string;
    kind: string;
    gain: number;
    state: string;
    audible: boolean;
    /**
     * **播放器真的在跑吗**（`state === 'started'`）。
     *
     * ⚠️ 它和 `audible` **必须分开看**：懒加载漏掉"半路加入"那一刀时，
     *    增益是亮的（`audible: true`）而播放器是 `stopped` —— 表面上一切正常，
     *    实际上一点声音都没有。冒烟盯着"亮着但没跑"的层。
     */
    running: boolean;
  }> {
    const out: Array<{
      cue: string;
      track: string;
      kind: string;
      gain: number;
      state: string;
      audible: boolean;
      running: boolean;
    }> = [];
    for (const [cueId, runtime] of this.runtimes) {
      const isActive = cueId === this.activeCue;
      const push = (layer: Layer, track: string, kind: string, on: boolean): void => {
        out.push({
          cue: cueId,
          track,
          kind,
          gain: layer.gain.gain.value,
          state: layer.player.state,
          audible: isActive && on,
          running: layer.player.state === 'started',
        });
      };
      const baseIds = [runtime.spec.track, ...(runtime.spec.overlays ?? [])];
      runtime.base.forEach((layer, i) => push(layer, baseIds[i] ?? '?', 'base', true));
      for (const entry of runtime.conditional) {
        const label = runtime.spec.layers?.[runtime.conditional.indexOf(entry)]?.track ?? '?';
        push(entry.layer, label, 'cond', matches(this.params, entry.when));
      }
      runtime.variants.forEach((entry, i) => {
        const label = runtime.spec.variant?.options[i]?.track ?? '?';
        push(entry.layer, label, 'variant', matches(this.params, entry.when));
      });
      runtime.random.forEach((layer, i) =>
        push(layer, runtime.spec.randomLayers?.pool[i] ?? '?', 'random', runtime.randomOn.has(i)),
      );
    }
    return out;
  }

  // -------------------------------------------------------------- 内部：加载 ----

  /**
   * 挑一个浏览器能解的源把 player 装满。先按 `canPlayType` 排序，再**依次真解**。
   * 全失败时给出能直接定位问题的错误，而不是裸的 `EncodingError`。
   */
  private async loadTrack(player: Player, track: TrackSpec, trackId: string): Promise<void> {
    const claimed = track.sources.filter(s => canDecodeMime(s.mime));
    const rest = track.sources.filter(s => !canDecodeMime(s.mime));

    const failures: string[] = [];
    for (const source of [...claimed, ...rest]) {
      try {
        await player.load(source.url);
        this.chosenSources.set(trackId, source);
        return;
      } catch (error) {
        failures.push(`${source.mime} ← ${source.url}\n      ${String(error)}`);
      }
    }

    throw new Error(
      t('audio.bgm.trackDecodeFailed', {
        track: trackId,
        failures: failures.join('\n    · '),
      }),
    );
  }

  private async makeLayer(
    trackId: string,
    loop: LoopRegion | undefined,
    cueId: string,
  ): Promise<Layer> {
    const track = this.score.tracks[trackId];
    if (!track) {
      throw new Error(t('audio.bgm.trackMissing', { cue: cueId, track: trackId }));
    }
    const player = new Player();
    await this.loadTrack(player, track, trackId);
    player.loop = true;
    const region: LoopRegion = loop ?? { start: 0, end: player.buffer.duration };
    player.loopStart = region.start;
    player.loopEnd = region.end;

    const gain = new Gain(0);
    player.connect(gain);
    gain.connect(this.master);
    // 记账：解码出来的 PCM 有多大（32 位浮点 × 声道数 × 采样数）
    this.tracksDecoded += 1;
    this.pcmBytes += player.buffer.length * player.buffer.numberOfChannels * 4;
    return { player, gain, region };
  }

  /**
   * 建一个 cue 的运行时。
   *
   * @param essential `true` = **只建"马上可能响"的那几层**：主轨（+ overlays）
   *   与 variant（整轨替换，缺一个就是静音）。条件层（`layers`）和随机池
   *   （`randomLayers.pool`）**不建** —— 它们由 `completeCue()` 在后台补。
   *
   *   ⚠️ 这两样的字节占比很悬殊：实测大厅那一 cue 的 17.9 MiB 里，随机池
   *   一个人占 13.5 MiB（6 条 spec-*），而它们**只在 energy > 0 时才会响**
   *   （也就是进了对局之后）。首屏等它解码纯属白等。
   */
  private async buildRuntime(cueId: string, spec: CueSpec, essential = false): Promise<void> {
    // 配了 variant 的 cue，实际播放的**只有** variant 选中的那一层。
    // 主轨只用来提供 bpm / 循环区间等元信息 —— 否则正位和逆位会同时响。
    const variantOnly = (spec.variant?.options.length ?? 0) > 0;
    if (variantOnly && spec.overlays?.length) {
      throw new Error(t('audio.bgm.variantOverlayConflict', { cue: cueId }));
    }
    const base: Layer[] = [];
    if (!variantOnly) {
      for (const trackId of [spec.track, ...(spec.overlays ?? [])]) {
        base.push(await this.makeLayer(trackId, spec.loop, cueId));
      }
    }

    // 条件层 / 变体 / 随机层都必须和主轨**同一循环区间** ——
    // 它们是同一首曲子的分层或变体，只有同偏移才对得上拍。
    const conditional: { layer: Layer; when: ParamBag; fade: number }[] = [];
    if (!essential) {
      for (const entry of spec.layers ?? []) {
        conditional.push({
          layer: await this.makeLayer(entry.track, spec.loop, cueId),
          when: entry.when,
          fade: entry.fade ?? CONDITIONAL_FADE,
        });
      }
    }

    // ⚠️ variant 属于"马上可能响"：整轨替换靠两个播放器同步跑，
    //    缺一个就是那一边彻底静音，所以 essential 也要建
    const variants: { layer: Layer; when: ParamBag }[] = [];
    for (const option of spec.variant?.options ?? []) {
      variants.push({ layer: await this.makeLayer(option.track, spec.loop, cueId), when: option.when });
    }

    const random: Layer[] = [];
    if (!essential) {
      for (const trackId of spec.randomLayers?.pool ?? []) {
        random.push(await this.makeLayer(trackId, spec.loop, cueId));
      }
    }

    this.built.add(cueId);
    if (!essential) this.complete.add(cueId);
    if (this.initialCueBuilt === null) this.initialCueBuilt = cueId;
    const primary = this.score.tracks[spec.track];
    if (!primary) throw new Error(t('audio.bgm.primaryTrackMissing', { cue: cueId }));
    this.runtimes.set(cueId, {
      spec,
      track: primary,
      base,
      conditional,
      variants,
      random,
      randomOn: new Set(),
      nextReroll: 0,
    });
  }

  // -------------------------------------------------------------- 内部：时钟 ----

  private tick(): void {
    if (!this.running || this.disposed) return;
    const now = this.audioNow();
    const horizon = now + LOOKAHEAD;

    // 延迟交接的时钟：等旧 cue 淡出 / 收尾播完
    if (this.pendingClock && this.pendingClock.at <= horizon) {
      const { at, runtime } = this.pendingClock;
      this.pendingClock = null;
      this.setClock(runtime, at);
    }

    if (this.pendingRestart) {
      const at = this.nextBarBoundaryAfter(now);
      if (at <= horizon) {
        this.pendingRestart = false;
        this.performRestart(at);
      }
    }

    if (this.pendingCue !== null) {
      const target = this.runtimes.get(this.pendingCue);
      if (target === undefined) {
        /**
         * ⚠️ **还没解好**（后台在补）：这一跳**留在原地等**（什么都不做，
         * `pendingCue` 不动）。
         *
         * 以前这里会走到 `beginTransition()`，而它开头就是
         * `if (!runtime) return;` —— 于是"切曲"被悄悄丢掉，音乐永远停在
         * 旧 cue 上。懒加载之后"目标 cue 还没好"是个正常状态，所以必须等；
         * 推进它的是 `warmRest()` 那条链，这里不必再排一次。
         */
      } else {
        const at = this.switchTimeFor(target, now);
        if (at <= horizon) {
          const cueId = this.pendingCue;
          this.pendingCue = null;
          this.beginTransition(cueId, at);
        }
      }
    }

    if (this.barDur <= 0) return;
    let index = this.nextBarIndex;
    for (;;) {
      const at = this.barEpoch + index * this.barDur;
      if (at > horizon) break;
      if (at < now - STALE) {
        index = Math.max(index + 1, Math.ceil((now - this.barEpoch) / this.barDur));
        continue;
      }
      this.scheduleBar(index, at);
      index++;
    }
    this.nextBarIndex = index;
  }

  /** 什么时候真的动手切：看目标 cue 的 `transitionIn.at`。 */
  private switchTimeFor(target: CueRuntime | undefined, now: number): number {
    const at = target?.spec.transitionIn?.at ?? DEFAULT_TRANSITION.at;
    return at === 'immediate' ? now + START_OFFSET : this.nextBarBoundaryAfter(now);
  }

  private nextBarBoundaryAfter(t: number): number {
    if (this.barDur <= 0) return t;
    const index = Math.ceil((t - this.barEpoch) / this.barDur - 1e-9);
    return this.barEpoch + Math.max(index, 0) * this.barDur;
  }

  private setClock(runtime: CueRuntime, epoch: number): void {
    this.barDur = (runtime.track.beatsPerBar * 60) / runtime.track.bpm;
    this.beatsPerBar = runtime.track.beatsPerBar;
    this.barEpoch = epoch;
    this.nextBarIndex = 0;
    this.generation++;
  }

  private scheduleBar(bar: number, time: number): void {
    const generation = this.generation;
    const beatsPerBar = this.beatsPerBar;
    const beatDur = this.barDur / beatsPerBar;

    for (let beat = 0; beat < beatsPerBar; beat++) {
      const beatTime = time + beat * beatDur;
      Draw.schedule(() => {
        if (generation !== this.generation) return;
        this.emit('beat', { time: beatTime, bar, beatInBar: beat, beatsPerBar });
      }, beatTime);
    }

    const barInPhrase = bar % this.phraseBars;
    Draw.schedule(() => {
      if (generation !== this.generation) return;
      this.emit('bar', { time, bar, barInPhrase });
      if (barInPhrase === 0) {
        this.emit('phrase', {
          time,
          bar,
          phrase: Math.floor(bar / this.phraseBars),
          bars: this.phraseBars,
        });
      }
    }, time);

    this.maybeRerollRandom(time);
  }

  /**
   * 到点了就重掷随机层的开关。**在小节边界执行**：淡入淡出切在乐句正中间会很难听。
   */
  private maybeRerollRandom(time: number): void {
    const runtime = this.activeCue === null ? undefined : this.runtimes.get(this.activeCue);
    const config = runtime?.spec.randomLayers;
    if (!runtime || !config || runtime.random.length === 0) return;
    if (time < runtime.nextReroll) return;

    const poolSize = runtime.random.length;
    const { min, max } = config.count(this.params);
    const lo = clamp(Math.floor(min), 0, poolSize);
    const hi = clamp(Math.floor(max), lo, poolSize);
    const wanted = randomInt(lo, hi);

    while (runtime.randomOn.size > wanted) {
      const index = pickRandom([...runtime.randomOn]);
      if (index === null) break;
      runtime.randomOn.delete(index);
      const layer = runtime.random[index];
      if (layer) this.ramp(layer, 0, time, config.fade);
    }

    const off = runtime.random.map((_, i) => i).filter(i => !runtime.randomOn.has(i));
    while (runtime.randomOn.size < wanted && off.length > 0) {
      const index = pickRandom(off);
      if (index === null) break;
      off.splice(off.indexOf(index), 1);
      runtime.randomOn.add(index);
      const layer = runtime.random[index];
      if (layer) this.ramp(layer, 1, time, config.fade);
    }

    runtime.nextReroll =
      time + config.reroll[0] + Math.random() * (config.reroll[1] - config.reroll[0]);
  }

  // -------------------------------------------------------------- 内部：过渡 ----

  /**
   * 按目标 cue 的 `transitionIn` 完成一次切换。
   *
   * 三种退场 × 三种进场。其中 `in = 'after'` 会把新 cue 的起播**和时钟交接**
   * 都推迟到旧的真正走完之后 —— 这就是「f9 先播收尾，完了立刻接 f0 引入」。
   */
  private beginTransition(cueId: string, switchAt: number): void {
    const runtime = this.runtimes.get(cueId);
    if (!runtime) return;

    const previousCue = this.activeCue;
    const previous = previousCue === null ? undefined : this.runtimes.get(previousCue);
    const spec = runtime.spec.transitionIn;
    const outMode = spec?.out ?? DEFAULT_TRANSITION.out;
    const inMode = spec?.in ?? DEFAULT_TRANSITION.in;

    // ---- 旧 cue 退场，并算出「交接时刻」 ----
    let handoff = switchAt;
    if (previous && previousCue !== cueId) {
      if (outMode === 'outro' && previous.spec.outro) {
        const outro = previous.spec.outro;
        handoff = switchAt + (outro.end - outro.start) + TAIL;
        // 播一次 outro 区间然后自然停止：给 duration 就能越过 loop
        for (const layer of layersOf(previous)) {
          // ⚠️ 必须临时关掉 loop：Tone 的 `Player._start` 在 loop=true 时
          // 会把 offset 夹回 `[loopStart, loopEnd]`。收尾段的 144.0 正好等于
          // `loopEnd`，于是被夹成 loopStart=36 —— 收尾会变成从头再来一遍。
          layer.player.loop = false;
          // stop 和 start 同一时刻在 Tone 里会打架，错开 1ms（听不出来）
          layer.player.stop(switchAt);
          layer.player.start(switchAt + 0.001, outro.start, outro.end - outro.start);
        }
        // 收尾播完正好是交接点，把旧 cue 的增益收干净（诊断和听感都更清楚）
        for (const layer of layersOf(previous)) this.ramp(layer, 0, handoff, 0);
      } else if (outMode === 'cut') {
        this.silence(previous, switchAt);
      } else {
        const dur = spec?.outFade ?? previous.spec.fadeOut ?? 0.4;
        handoff = switchAt + dur + TAIL;
        for (const layer of layersOf(previous)) {
          this.ramp(layer, 0, switchAt, dur);
          layer.player.stop(switchAt + dur + TAIL);
        }
      }
    }

    // ---- 新 cue 进场 ----
    // ⚠️ activeCue 必须在 startCueAt 之前设好：它会调 refreshLayers，
    // 而那些函数会检查「这是不是当前 cue」，设晚了就会被早退掉、增益全是 0。
    this.activeCue = cueId;

    const startAt = inMode === 'after' ? handoff : switchAt;
    this.startCueAt(runtime, startAt, inMode === 'fade' ? (spec?.inFade ?? runtime.spec.fadeIn ?? 0.4) : 0);

    // ---- 时钟交接 ----
    // 旧的还在响（淡出 / 收尾）时不能立刻换时钟，否则那段时间小节事件会断档。
    if (startAt > switchAt + 1e-3) {
      this.pendingClock = { at: startAt, runtime };
    } else {
      this.pendingClock = null;
      this.setClock(runtime, startAt);
    }

    const seq = ++this.transitionSeq;
    Draw.schedule(() => {
      if (seq !== this.transitionSeq) return;
      this.emit('cue', { time: startAt, cue: cueId, previous: previousCue });
    }, startAt);
  }

  /** 启动一个 cue 的所有层；`fade > 0` 时主轨从 0 淡入。 */
  private startCueAt(runtime: CueRuntime, time: number, fade: number): void {
    const startAt = runtime.spec.playFrom ?? runtime.spec.loop?.start ?? runtime.base[0]?.region.start ?? 0;
    this.posAtEpoch = startAt;

    for (const layer of runtime.base) {
      layer.player.loop = true;
      layer.player.stop(time);
      layer.player.start(time, startAt);
      if (fade > 0) this.ramp(layer, 1, time, fade);
      else this.ramp(layer, 1, time, 0);
    }

    // 条件层 / 变体 / 随机层：先起播并静音，增益交给 refreshLayers
    for (const { layer } of [...runtime.conditional, ...runtime.variants]) {
      layer.player.loop = true;
      layer.player.stop(time);
      layer.player.start(time, startAt);
      layer.gain.gain.cancelScheduledValues(time);
      layer.gain.gain.setValueAtTime(0, time);
    }
    for (const layer of runtime.random) {
      layer.player.loop = true;
      layer.player.stop(time);
      layer.player.start(time, startAt);
      layer.gain.gain.cancelScheduledValues(time);
      layer.gain.gain.setValueAtTime(0, time);
    }
    runtime.randomOn.clear();
    runtime.nextReroll = time;

    this.refreshLayers(runtime, time);
  }

  /**
   * 把「参数驱动的增益」刷新一遍。**只改增益，不碰播放位置** ——
   * 所以切变体不会重播、不会丢位置，这就是「isDying 有首次」的前提。
   */
  private refreshLayers(runtime: CueRuntime, time: number): void {
    if (runtime !== (this.activeCue === null ? undefined : this.runtimes.get(this.activeCue))) return;

    for (const entry of runtime.conditional) {
      this.ramp(entry.layer, matches(this.params, entry.when) ? 1 : 0, time, entry.fade);
    }

    // 互斥变体：按顺序第一个匹配的响，其余静音
    const options = runtime.spec.variant?.options ?? [];
    const chosen = options.findIndex(option => matches(this.params, option.when));
    runtime.variants.forEach((entry, index) => {
      this.ramp(entry.layer, index === chosen ? 1 : 0, time, runtime.spec.variant?.fade ?? 0.4);
    });
  }

  /** 在小节边界把当前 cue 拉回循环起点。 */
  private performRestart(time: number): void {
    const runtime = this.activeCue === null ? undefined : this.runtimes.get(this.activeCue);
    if (runtime) {
      // 所有层一起回到循环起点，否则分层之间会错拍。
      // 用 loop.start 而不是 playFrom：重启是「回到循环开头」，不是重播引入段。
      const region = runtime.spec.loop;
      this.posAtEpoch = region?.start ?? 0;
      for (const layer of layersOf(runtime)) {
        layer.player.restart(time, region?.start ?? layer.region.start);
      }
    }
    const generation = ++this.generation;
    this.barEpoch = time;
    this.nextBarIndex = 0;
    Draw.schedule(() => {
      if (generation !== this.generation) return;
      this.emit('restart', { time });
    }, time);
  }

  private silence(runtime: CueRuntime, time: number): void {
    for (const layer of layersOf(runtime)) {
      layer.gain.gain.cancelScheduledValues(time);
      layer.gain.gain.setValueAtTime(0, time);
      layer.player.stop(time);
    }
    runtime.randomOn.clear();
  }

  private ramp(layer: Layer, target: number, time: number, duration: number): void {
    const param = layer.gain.gain;
    param.cancelScheduledValues(time);
    param.setValueAtTime(param.value, time);
    if (duration <= 0) param.setValueAtTime(target, time);
    else param.linearRampToValueAtTime(target, time + duration);
  }
}

/** 一个 cue 的所有层。**任何遍历都必须用它**，漏一层就是「切走了还在响」。 */
function layersOf(runtime: CueRuntime): readonly Layer[] {
  return [
    ...runtime.base,
    ...runtime.conditional.map(entry => entry.layer),
    ...runtime.variants.map(entry => entry.layer),
    ...runtime.random,
  ];
}
